import {computed, Ref, ref} from "vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {ProbeDraft, ProbeFinding, ProbeView} from "@/medication-checkup/interfaces/probe";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useMissingAnswers} from "@/medication-checkup/composables/useMissingAnswers";

/**
 * What a draft is recorded as: the effect picked, or for « Autre » the words
 * the pharmacist typed, as typed.
 */
export const probeLabel = (draft: ProbeDraft, other: string | null): string => {
    return draft.option === other ? draft.detail.trim() : draft.option;
};

/**
 * What the scenes of the probes share: the answer and the findings drafted
 * until the closing action submits them, and the submission itself.
 *
 * Each probe keeps its own scene — its icon, its place in the course — and
 * plays the same mechanic through this composable: one question asked to
 * find out whether something is worth digging into and, on a « yes », what
 * was observed over which treatments. Answering is the scene's own state, as
 * it is in the Girerd questionnaire: the closing action, held until the
 * question is answered, submits it all at once.
 *
 * @param root The root element of the scene, where what is missing is pointed at.
 */
export const useProbeScene = (props: SceneProps<ProbeView>, root: Ref<HTMLElement | null>) => {
    const {bubbleShown, cardShown, clicked, view, text, actions, closing, acted, act} = useScene(
        props,
        {card: true}
    );

    /**
     * The answer given so far — local until the closing action submits it.
     */
    const answer = ref<boolean | null>(null);

    /**
     * The findings drafted so far, on a « yes » — local until the closing
     * action submits them. Resets with the scene, as the selection of the
     * collection does.
     */
    const drafts = ref<ProbeDraft[]>([]);

    /**
     * What holds the closing action: the question unanswered, or « Autre »
     * picked with nothing written.
     */
    const missing = computed(() => [
        ...answer.value === null ? ['answer'] : [],
        ...drafts.value.some(draft => draft.option === view.value?.other && draft.detail.trim() === '') ? ['other'] : [],
    ]);

    const {blocked, flagged, warning, flag} = useMissingAnswers(missing, root, refs => {
        return refs.includes('answer') ? '1 question sans réponse' : "Précisez l'effet « Autre »";
    });

    /**
     * Answer the question — local only: the closing action submits it. A
     * « no » drops the drafts, which only ride on a « yes ».
     */
    const give = (given: boolean) => {
        answer.value = given;

        if (!given) {
            drafts.value = [];
        }
    };

    /**
     * A draft as the handler expects it: its label in the words of the
     * catalogue, of Vidal or of the pharmacist, and the treatments suspected.
     */
    const toFinding = (draft: ProbeDraft): ProbeFinding => ({
        label: probeLabel(draft, view.value?.other ?? null),
        line_ids: draft.line_ids
    });

    /**
     * Take the closing action, submitting the answer and its findings.
     */
    const close = (action: MedicationCheckupAction) => act(action, {
        answer: answer.value,
        findings: answer.value === true ? drafts.value.map(toFinding) : []
    });

    return {
        bubbleShown,
        cardShown,
        clicked,
        answer,
        drafts,
        view,
        text,
        actions,
        closing,
        acted,
        blocked,
        flagged,
        warning,
        flag,
        give,
        act: close
    };
};
